/**
 * Robust opener for the Shopify Inbox chat widget.
 *
 * The widget is a third-party app embed (currently <shopify-chat>, wrapping
 * a lazily-mounted <shopify-agent-runtime> once first opened — Shopify has
 * already renamed/restructured this once, from an earlier #ShopifyChat id).
 * Its markup nests shadow DOM *multiple levels deep*, and which level holds
 * the real activator button changes once the runtime mounts on first open —
 * a scan that only pierces one shadow boundary finds the button once, then
 * goes blind after the first open/close cycle. There is no official public
 * JS API to open it, so this instead:
 *
 * 1. Recursively walks every shadow root on the page, at any depth, fresh
 *    on every call — cheap enough for a click handler, and avoids caching a
 *    reference that the widget may replace out from under us.
 * 2. Within each shadow root, prefers an element with `part="activator"` —
 *    Shopify's own deliberately-public styling/interaction hook (exposed
 *    via `exportparts` on the host) — before falling back to matching a
 *    visible button semantically (aria-label/aria-controls/text mentioning
 *    "chat"/"inbox"/"message").
 * 3. Simulates a full pointer gesture (not just `click`) in case the
 *    widget's internal handling expects more than a bare click event.
 *
 * Any future "open chat" button anywhere in the theme should call
 * window.openShopifyChat() rather than reimplementing this.
 */
(function() {
  if (window.openShopifyChat) return;

  const CHAT_NAME_HINTS = ['chat', 'inbox', 'message'];
  const MAX_SHADOW_DEPTH = 8;

  // Shopify Inbox's launcher is configured here with button_text: "no_text",
  // so the activator carries no visible text and is frequently a
  // `<div role="button">` rather than a real `<button>`. Match both element
  // kinds so the text-less launcher is still found when the (preferred)
  // part="activator" hook is absent.
  const CONTROL_SELECTOR = 'button, [role="button"]';

  function isVisible(el) {
    return el.getClientRects().length > 0;
  }

  function isLikelyChatToggle(button) {
    const label = (
      (button.getAttribute('aria-label') || '') + ' ' +
      (button.getAttribute('aria-controls') || '') + ' ' +
      (button.textContent || '')
    ).toLowerCase();
    return CHAT_NAME_HINTS.some((hint) => label.includes(hint));
  }

  function findToggleInRoot(root) {
    const partMatch = root.querySelector('[part~="activator"]');
    if (partMatch && isVisible(partMatch)) return partMatch;

    const controls = Array.from(root.querySelectorAll(CONTROL_SELECTOR)).filter(isVisible);
    if (controls.length === 0) return null;

    const expandableChatButton = controls.find((btn) => btn.hasAttribute('aria-expanded') && isLikelyChatToggle(btn));
    if (expandableChatButton) return expandableChatButton;

    const chatButton = controls.find(isLikelyChatToggle);
    if (chatButton) return chatButton;

    const anyExpandable = controls.find((btn) => btn.hasAttribute('aria-expanded'));
    if (anyExpandable) return anyExpandable;

    // Some widget states expose only a single top-level control with no
    // identifying text/label — if that's the only one here, it's it.
    return controls.length === 1 ? controls[0] : null;
  }

  /**
   * Recursively collects every open shadow root reachable from `root`, at
   * any nesting depth — required because which level the real activator
   * lives at changes once the widget's runtime mounts on first open.
   */
  function collectShadowRoots(root, depth, out) {
    if (depth > MAX_SHADOW_DEPTH) return out;

    const elements = root.querySelectorAll('*');
    for (const el of elements) {
      if (el.shadowRoot) {
        out.push(el.shadowRoot);
        collectShadowRoots(el.shadowRoot, depth + 1, out);
      }
    }
    return out;
  }

  function locateChatToggleButton() {
    const shadowRoots = collectShadowRoots(document, 0, []);
    // The two *targeted* passes below (the public part hook and a control
    // that semantically names chat) also scan the light DOM, in case a future
    // Inbox version renders its launcher outside shadow DOM. The greedy
    // fallback after them stays shadow-roots-only, so it can never grab an
    // unrelated expandable/lone control from the rest of the page.
    const namedSearchRoots = [document, ...shadowRoots];

    // Prefer the `part="activator"` hook wherever it's found before falling
    // back to heuristic matching anywhere, so a heuristic match at a
    // shallower level never wins over the real hook at a deeper one.
    for (const root of namedSearchRoots) {
      const match = root.querySelector('[part~="activator"]');
      if (match && isVisible(match)) return match;
    }

    // A control whose label/text names chat/inbox/message, wherever it lives.
    for (const root of namedSearchRoots) {
      const controls = Array.from(root.querySelectorAll(CONTROL_SELECTOR)).filter(isVisible);
      const named = controls.find((el) => el.hasAttribute('aria-expanded') && isLikelyChatToggle(el))
        || controls.find(isLikelyChatToggle);
      if (named) return named;
    }

    // Greedy heuristic fallback (expandable control, then a lone control) —
    // restricted to shadow roots, where the widget actually lives.
    for (const root of shadowRoots) {
      const match = findToggleInRoot(root);
      if (match) return match;
    }

    // Legacy iframe-based launcher fallback (older Inbox embed versions).
    const iframe = document.querySelector('#dummy-chat-button-iframe, iframe[id*="chat" i]');
    if (iframe) {
      try {
        const doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
        const button = doc && doc.querySelector('button');
        if (button) return button;
      } catch (e) {
        // Cross-origin iframe — inaccessible, nothing more we can do here.
      }
    }

    return null;
  }

  /**
   * A bare `element.click()` only dispatches a `click` event. Some web
   * components key their interaction handling off the full pointer gesture,
   * not just `click`. Dispatch the full sequence, composed so it crosses
   * the shadow boundary and reaches any listener on/above the host.
   */
  function simulateClick(el) {
    const rect = el.getBoundingClientRect();
    const eventInit = {
      bubbles: true,
      cancelable: true,
      composed: true,
      view: window,
      clientX: rect.left + rect.width / 2,
      clientY: rect.top + rect.height / 2
    };

    ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'].forEach((type) => {
      const EventCtor = type.startsWith('pointer') && window.PointerEvent ? PointerEvent : MouseEvent;
      el.dispatchEvent(new EventCtor(type, eventInit));
    });
  }

  /**
   * Opens (or closes/re-opens) the chat widget. Safe to call before the
   * widget has finished loading — retries for a few seconds since app
   * embeds can attach after the rest of the page. Always searches fresh
   * rather than caching a button reference, since the widget itself
   * replaces its DOM structure between closed and open states.
   */
  window.openShopifyChat = function openShopifyChat() {
    const button = locateChatToggleButton();
    if (button) {
      simulateClick(button);
      return;
    }

    const MAX_ATTEMPTS = 20;
    const INTERVAL_MS = 300;
    let attempts = 0;

    const retryTimer = setInterval(() => {
      attempts += 1;
      const retryButton = locateChatToggleButton();

      if (retryButton) {
        clearInterval(retryTimer);
        simulateClick(retryButton);
      } else if (attempts >= MAX_ATTEMPTS) {
        clearInterval(retryTimer);
        console.warn('[chat-widget] Could not locate the chat widget toggle button.');
      }
    }, INTERVAL_MS);
  };

  // Auto-wire any button/link meant to open chat, so theme editor content
  // (custom-liquid blocks, etc.) never needs its own inline script again.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('#chat-link, [data-open-chat]');
    if (!trigger) return;

    event.preventDefault();
    window.openShopifyChat();
  });
})();
